import * as React from "react";
import {option} from "ts-option";
import DatePickerComponent from "../ui/datepicker/DatePickerComponent";
import DateUtils from "../../utils/DateUtils";
import AppointmentDetails from "../../model/appointment/AppointmentDetails";
import {RootState} from "../../store/reducer/root/RootState";
import {fetchUserLocations} from "../../store/action/userLocation/userLocation";
import {Dispatch} from "redux";
import {RouteComponentProps, withRouter} from "react-router";
import {connect} from "react-redux";
import User from "../../model/user/User";
import {getUserLocation} from "../../store/view/user";
import MapUtils from "../../utils/MapUtils";
import {Icon} from "../../hiberUiKit/src/atoms/Icon/Icon";
import LocalStorage from "../../store/local/LocalStorage";
import {DayViewFilters} from "../../store/local/model/DayViewFilters";
import DayViewTimeLine from "./DayViewTimeLine";
import ButtonSecondary from "../../hiberUiKit/src/atoms/Buttons/ButtonSecondary";
import ButtonPrimary from "../../hiberUiKit/src/atoms/Buttons/ButtonPrimary";
import IconLarge from "../../hiberUiKit/src/atoms/Icon/IconLarge";
import DayViewEngineersFilter from "./DayViewEngineersFilter";
import {objectIterator} from "../../utils/DictionaryUtils";
import DayViewMap from "./DayViewMap";
import Checkbox from "../../hiberUiKit/src/atoms/Checkbox/Checkbox";
import {notifyBugsnagAndShowToast} from "../../utils/Utils";
import {OnSuccessData} from "../../store/action/actionHelpers";
import {fetchJobsAppointmentsInDateRange, fetchLeadsAppointmentsInDateRange} from "../../store/action/appointment/appointment";
import moment = require("moment");
import classNames = require("classnames");
import deepEqual = require("deep-equal");

interface State {
	/**
	 * Date of displayed appointments
	 */
	appointmentDate: moment.Moment,
	/**
	 * Appointments to show on a map (with locations).
	 */
	appointments: AppointmentDetails[],
	/**
	 * Sales engineers with home GPS locations.
	 */
	salesEngineers: User[],
	/**
	 * Lattitude and langitude passed to map.
	 */
	center: {
		lat: number,
		lng: number
	},
	/**
	 * Map view state:
	 * 0 - hidden
	 * 1 - half-visible
	 * 2 - full-visible
	 */
	mapState: number,
	/**
	 * Indicates if engineer homes are displayed on map
	 */
	displayHomes: boolean,
	/**
	 * Indicates if engineer locations are displayed on map
	 */
	displayLocations: boolean,
	/**
	 * Indicates if appointments are displayed on map
	 */
	displayAppointments: boolean;
	/**
	 * Day view stored filters object, containing date selected by user.
	 */
	storedData: DayViewFilters;
	/**
	 * Indicates if calendar is visible.
	 */
	calendarVisible: boolean;
	/**
	 * Indicates if list of SE shows only engineers with appointments.
	 */
	filterWithAppointments: boolean;
}

interface ReduxProps {
	/**
	 * Sales engineers (without home location).
	 */
	salesEngineers: User[]
}

interface DispatchProps {
	fetchUserLocations: (roles: string[]) => void;
	fetchLeadsAppointmentsInDateRange: (dateFrom: moment.Moment,
		dateTo: moment.Moment,
		onSuccess: OnSuccessData<AppointmentDetails[]>) => void
	fetchJobsAppointmentsInDateRange: (dateFrom: moment.Moment,
		dateTo: moment.Moment,
		onSuccess: OnSuccessData<AppointmentDetails[]>) => void
}

type Props = ReduxProps & DispatchProps & RouteComponentProps<{}>

/**
 * Daily plan component
 */
class DayView extends React.Component<Props, State> {
	/**
	 * Pulling timer identifier.
	 * @type {number}
	 */
	private pullingTimerId: number = null;

	constructor(props: Props) {
		super(props);
		const storedData = LocalStorage.getDayViewFilters();
		this.state = {
			appointmentDate: moment(storedData.date),
			appointments: [],
			salesEngineers: [],
			center: {
				lat: 51.919516,
				lng: -0.2850921
			},
			mapState: storedData.mapState,
			displayHomes: storedData.displayHomes,
			displayLocations: storedData.displayLocations,
			displayAppointments: storedData.displayAppointments,
			storedData: storedData,
			calendarVisible: false,
			filterWithAppointments: storedData.filterSEWithAppointments
		};
		this.changeDay = this.changeDay.bind(this);
	}

	shouldComponentUpdate(nextProps: Props, nextState: State) {
		return !deepEqual(nextProps, this.props) || !deepEqual(nextState, this.state)
	}

	componentDidMount() {
		this.fetchAppointments();
		this.populateEngineersLocations(this.props.salesEngineers);
		this.props.fetchUserLocations(["SALES_ENGINEER"]);
		document.body.classList.add("not-scrollable");
	}

	componentWillUnmount() {
		this.pullingTimerId && window.clearInterval(this.pullingTimerId);
		document.body.classList.remove("not-scrollable");
	}

	componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>) {
		if (this.state.mapState > 0 && !this.pullingTimerId) {
			if (!deepEqual(prevState.salesEngineers, this.state.salesEngineers)) {
				this.props.fetchUserLocations(["SALES_ENGINEER"]);
			}
			this.pullingTimerId = window.setInterval(() => this.props.fetchUserLocations(["SALES_ENGINEER"]), 15 * 1000);
		}
		else if (this.state.mapState === 0 && this.pullingTimerId) {
			window.clearInterval(this.pullingTimerId);
			this.pullingTimerId = null;
		}

		if (!deepEqual(prevProps.salesEngineers, this.props.salesEngineers)) {
			this.setState({salesEngineers: this.props.salesEngineers});
			this.populateEngineersLocations(this.props.salesEngineers);
		}
	}

	/**
	 * Populate sales engineers home address GPS locations.
	 * @param {User[]} users
	 */
	private populateEngineersLocations(users: User[]) {
		if (users.length === 0) {
			return;
		}
		const promises = users.map(MapUtils.populateUserLocation);
		Promise.all(promises).then(results => {
			const found: User[] = [];
			results.forEach(user => {
				if (user.data.homeAddressLocation.isDefined || getUserLocation(user.id).isDefined) {
					found.push(user)
				}
			});
			this.setState({salesEngineers: found})
		})
	}

	/**
	 * Populate appointments details with locations.
	 * @param {AppointmentDetails[]} appointments
	 */
	private populateAppointmentsLocations = (appointments: AppointmentDetails[]) => {
		if (appointments.length === 0) {
			return;
		}
		const promises = appointments.map(MapUtils.populateAppointmentLocation);
		Promise.all(promises).then(results => {
			const found: AppointmentDetails[] = [];
			//use this to show invalid addresses
			const notFound: AppointmentDetails[] = [];
			results.map(details => {
				if (details.location.isDefined) {
					found.push(details)
				}
				else {
					notFound.push(details);
				}
			});
			const map: { [appId: string]: AppointmentDetails } = {};
			this.state.appointments.forEach(a => map[a.appointment.id] = a);
			found.forEach(a => map[a.appointment.id] = a);
			this.setState({appointments: this.state.appointments.concat(objectIterator(map))});
		})
	}

	render() {
		const mapClass = classNames({
			'c-day-view-map': true,
			'half-visible': this.state.mapState == 1,
			'full-visible': this.state.mapState == 2
		});
		const datePickerWrapperClass = classNames({
			"c-day-view_date-picker_wrapper": true,
			"hidden": !this.state.calendarVisible
		});
		return (
			<div className={"c-day-view"}>
				<div className={"c-day-view_background"}/>
				<DayViewTimeLine selectedDay={this.state.appointmentDate}
								 onDayChange={this.changeDay}
								 filterWithAppointments={this.state.filterWithAppointments}
								 appointments={this.state.appointments}/>
				<div className={mapClass}>
					<div className="c-day-view_map-buttons"
						 onClick={() => this.changeMapState(this.state.mapState === 2 ? 1 : 2)}>
						<IconLarge icon={Icon.ArrowLeft} variant={"burgundy"}/>
						<IconLarge icon={Icon.ArrowRight} variant={"burgundy"}/>
					</div>
					<DayViewMap
						center={this.state.center}
						appointments={this.state.appointments}
						displayAppointments={this.state.displayAppointments}
						displayHomes={this.state.displayHomes}
						displayLocations={this.state.displayLocations}
						salesEngineers={this.state.salesEngineers}/>
				</div>
				{
					this.state.mapState > 0 && <div className={"c-map-checkboxes"}>
						<Checkbox label={"Engineer homes"}
								  checked={this.state.displayHomes}
								  onChange={() => this.changeDisplayingHomes(!this.state.displayHomes)}/>
						<Checkbox label={"Engineer current location"}
								  checked={this.state.displayLocations}
								  onChange={() => this.changeDisplayingLocations(!this.state.displayLocations)}/>
						<Checkbox label={"Appointments"}
								  checked={this.state.displayAppointments}
								  onChange={() => this.changeDisplayingAppointments(!this.state.displayAppointments)}/>
					</div>
				}
				<div className={datePickerWrapperClass}>
					<DayViewEngineersFilter checked={this.state.filterWithAppointments}
											onChange={(value) => this.changeSEWithAppointmentFilter(value)}/>
					<DatePickerComponent
						inline
						customClass={"c-day-view_date-picker"}
						dateFormat={DateUtils.DATE_FORMAT}
						value={option(this.state.appointmentDate)}
						onChange={value => this.changeDay(value.orNull)}/>
				</div>
				<div className={"c-day-view_date-picker_button"}>
					<ButtonSecondary label={`${this.state.calendarVisible ? "Hide calendar" : "Show calendar"}`}
									 onClick={() => this.setState({calendarVisible: !this.state.calendarVisible})}/>
				</div>
				<div className={"c-day-view_map_hide-button"}>
					<ButtonPrimary label={this.state.mapState === 0 ? "Show map" : "Hide map"}
								   onClick={() => this.changeMapState(this.state.mapState >= 1 ? 0 : 1)}/>
				</div>
			</div>
		);
	}

	changeDay(date: moment.Moment) {
		this.setState({appointmentDate: date, storedData: this.state.storedData.updateDate(date)}, this.fetchAppointments);
	}

	changeMapState(value: number) {
		this.setState({mapState: value, storedData: this.state.storedData.updateMapState(value)})
	}

	changeDisplayingHomes(value: boolean) {
		this.setState({displayHomes: value, storedData: this.state.storedData.updateHomes(value)})
	}

	changeDisplayingLocations(value: boolean) {
		this.setState({displayLocations: value, storedData: this.state.storedData.updateLocations(value)})
	}

	changeDisplayingAppointments(value: boolean) {
		this.setState({displayAppointments: value, storedData: this.state.storedData.updateAppointments(value)})
	}

	changeSEWithAppointmentFilter(value: boolean) {
		this.setState({filterWithAppointments: value, storedData: this.state.storedData.updateSEFilter(value)})
	}

	fetchAppointments = () => {
		const date = this.state.appointmentDate
		this.setState({appointments: []}, () => {
			this.props.fetchLeadsAppointmentsInDateRange(date, date, this.populateAppointmentsLocations);
			this.props.fetchJobsAppointmentsInDateRange(date, date, this.populateAppointmentsLocations)
		});
	}
}

function mapStateToProps(state: RootState) {
	return {
		salesEngineers: state.user.users.map(users => users.filter(u => !u.disabled && u.isSalesEngineer())).getOrElse([]),
	}
}

function mapDispatchToProps(dispatch: Dispatch<any>) {
	return {
		fetchUserLocations: (roles: string[]) => dispatch(fetchUserLocations(roles, () => {}, () => {})),
		fetchLeadsAppointmentsInDateRange: (dateFrom: moment.Moment,
			dateTo: moment.Moment,
			onSuccess: OnSuccessData<AppointmentDetails[]>) => dispatch(
			fetchLeadsAppointmentsInDateRange(dateFrom, dateTo, onSuccess,
				e => notifyBugsnagAndShowToast(e, "Fetch Leads appointments"))),
		fetchJobsAppointmentsInDateRange: (dateFrom: moment.Moment,
			dateTo: moment.Moment,
			onSuccess: OnSuccessData<AppointmentDetails[]>) => dispatch(
			fetchJobsAppointmentsInDateRange(dateFrom, dateTo, onSuccess,
				e => notifyBugsnagAndShowToast(e, "Fetch Jobs appointments")))
	}
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(DayView));
